import { IconButton, Menu, createDataTableColumnVisibility } from "@carbon6/c6-frontend-design";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
  type ReactNode,
} from "react";
import { useNavigate, useSearchParams } from "react-router";
import {
  cancelDatasetUpload,
  deleteDatasetUpload,
  getDatasetUpload,
  reprocessDatasetUpload,
} from "@/api";
import type { UserRole } from "@/app";
import { PaginationFooter, ResourceBrowserLayout } from "@/components/browse";
import { FilterResultCount } from "@/components/filter";
import { HatTextModal } from "@/components/modal";
import { BulkActionBar } from "@/components/table/BulkActionBar";
import bulkActionBarStyles from "@/components/table/BulkActionBar.module.css";
import { HatDataTable } from "@/components/table/HatDataTable";
import { createLogger } from "@/logging";
import { usePageParam } from "@/app/usePageParam";
import { uploadWindows } from "@/page/upload/uploadWindow";
import { isForeignLiveLease, uploadEngine } from "@/upload";
import { applyUrlPageToSource, mirrorSourcePageToUrl } from "@/utils";
import { StagedFilesReviewDialog } from "./components/StagedFilesReviewDialog";
import { datasetUploadColumns } from "./datasetUploadColumns";
import { DatasetUploadFilterBar } from "./DatasetUploadFilterBar";
import {
  readDatasetUploadFilters,
  writeDatasetUploadFilters,
  type DatasetUploadFilterState,
} from "./datasetUploadFilters";
import { resolveErrorMessage } from "./datasetUploadFormatters";
import { DatasetUploadTableSource } from "./DatasetUploadTableSource";
import type { DatasetUploadTableRow } from "./DatasetUploadTableRow";
import { isDeletableUpload, isRetryableUpload, uploadRetryAction } from "./statusFilterGroups";
import { tableStripError } from "@/components/table/tableStateMessages";

const defaultPageSize = 20;
const logger = createLogger("page/datasetUpload");

type UploadJobRef = { uuid: string; displayName: string };

type DatasetUploadStatusPanelProps = {
  currentUserRole: UserRole | null;
  // 내 member uuid. 잡 생성자와 대조해 취소·기록 삭제를 허용한다. 세션을 복원하기 전이면 null.
  currentUserMemberUuid: string | null;
};

// 데이터 업로드 현황. Datasets 페이지의 한 탭이다. 이 패널이 목록, 상태 필터, 행 액션,
// 다이얼로그를 모두 소유한다. 페이지는 탭 정책만 가진다(GpuQuota 의 패널 분리와 같은 구조).
//
// `?page=`·`?scope=`·`?status=` 는 이 패널이 소유한다. 이 탭이 활성인 동안 데이터셋 소스는
// 렌더되지 않는다. 그래서 파라미터 소유자가 겹치지 않는다. DatasetPage 는 uploads 탭에서
// 소스 연결을 건너뛴다.
// 이 축들은 이 탭에만 적용된다. 그래서 필터 바는 페이지 belowTabs 가 아니라
// ResourceBrowserLayout filters 슬롯에 둔다(rules.md §17).
export function DatasetUploadStatusPanel({
  currentUserRole,
  currentUserMemberUuid,
}: DatasetUploadStatusPanelProps): ReactNode {
  const navigate = useNavigate();
  const { page, setPage } = usePageParam();
  const [searchParams, setSearchParams] = useSearchParams();

  // 전체 업로드 조회(scope=all)는 admin 전용이다. 잡의 취소·기록 삭제는 생성자 또는 admin 만 한다.
  // 서버가 행마다 권한 플래그를 보내지 않으므로, 생성자 uuid 를 내 member uuid 와 대조한다.
  const isAdmin = currentUserRole === "admin";
  const canViewAllUploads = isAdmin;
  const canManageUpload = (row: DatasetUploadTableRow): boolean =>
    isAdmin ||
    (row.created_by_member_uuid !== null && row.created_by_member_uuid === currentUserMemberUuid);
  // 조회 축(범위, 상태 그룹)은 URL 이 소유한다. 전체 업로드는 admin 전용이다. 그래서 권한이 없으면
  // URL 에 값이 있어도 "내 업로드" 로 바꾼다. 컨트롤(스위처, 드롭다운)과 조건 칩이 같은 값을 읽는다.
  const filters = useMemo((): DatasetUploadFilterState => {
    const parsed = readDatasetUploadFilters(searchParams);
    return canViewAllUploads ? parsed : { ...parsed, scope: "me" };
  }, [searchParams, canViewAllUploads]);
  const { scope: uploadScope, statusGroup } = filters;
  const [tableColumnVisibility] = useState(() =>
    createDataTableColumnVisibility(datasetUploadColumns),
  );
  // staged 검토·완료 다이얼로그의 대상. 행 액션이나 ?review= 딥링크로 연다. 창에서 진행 중이
  // 아닌 서버 잡(이전 세션, 완료 대기)을 검토하고 완료할 때 쓴다. 진행 중 업로드의 완료 검토는
  // 업로드 창이 소유한다.
  const [reviewTarget, setReviewTarget] = useState<UploadJobRef | null>(null);
  const [cancelTarget, setCancelTarget] = useState<UploadJobRef | null>(null);
  const [cancelBusy, setCancelBusy] = useState(false);
  const [cancelErrorMessage, setCancelErrorMessage] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<UploadJobRef | null>(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteErrorMessage, setDeleteErrorMessage] = useState<string | null>(null);
  // 재처리·재시도가 진행 중인 import uuid. 더보기 "재시도" 액션을 disabled 로 표시할 때 쓴다.
  const [retryingUuids, setRetryingUuids] = useState<readonly string[]>([]);
  // 재시도(reprocess) 실패 메시지. 목록 에러 영역에 표시한다. 목록 로드 에러와는 별개 값이다.
  const [retryErrorMessage, setRetryErrorMessage] = useState<string | null>(null);

  // 언마운트 뒤 도착한 응답이 상태를 바꾸지 않게 하는 가드. StrictMode 재마운트에서 다시 켠다.
  const aliveRef = useRef(true);
  useEffect(() => {
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
    };
  }, []);

  // 소스는 행을 만들 때 액션 빌더를 호출한다. 그래서 최신 클로저를 ref 로 교체해 전달한다.
  // 소스 자체는 패널 수명 동안 한 번만 만든다.
  const rowActionsRef = useRef<(row: DatasetUploadTableRow) => ReactNode>(() => null);
  const rowClickRef = useRef<(row: DatasetUploadTableRow) => void>(() => undefined);
  const [source] = useState(
    () =>
      new DatasetUploadTableSource({
        pageSize: defaultPageSize,
        rowActionsBuilder: (row) => rowActionsRef.current(row),
        rowClickHandler: (row) => rowClickRef.current(row),
        resolveErrorMessage,
        initialScope: uploadScope,
        initialStatusGroup: statusGroup,
      }),
  );
  const snapshot = useSyncExternalStore(source.subscribe, source.getSnapshot);

  // URL 조건이 바뀌면(컨트롤, 칩, 뒤로가기) 소스에 전달한다. setter 는 같은 값이면 no-op 이다.
  // 그래서 첫 마운트에서는 다시 조회하지 않는다. 생성 옵션이 이미 그 값을 가진다.
  // 값이 바뀌면 source 가 1페이지부터 다시 받는다.
  useEffect(() => {
    void source.setScope(uploadScope);
  }, [source, uploadScope]);
  useEffect(() => {
    void source.setStatusGroup(statusGroup);
  }, [source, statusGroup]);

  const handleFiltersChange = useCallback(
    (next: DatasetUploadFilterState): void => {
      setSearchParams((prev) => writeDatasetUploadFilters(prev, next), { replace: true });
    },
    [setSearchParams],
  );

  // 목록 새로고침 헬퍼. 업로드 창(생성, 재개, 재시도, 완료)이 닫히거나 행을 취소하면 서버 목록을
  // 다시 조회한다.
  const refreshList = useCallback((): void => void source.refresh(), [source]);

  useEffect(() => {
    // URL의 page(딥링크·뒤로가기 복귀)에서 시작한다.
    void source.goToPage(page);
    return () => {
      source.dispose();
    };
    // 최초 1회만 실행한다. 이후 URL page 를 따라가는 일은 아래 효과가 한다.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [source]);

  // 업로드 잡의 상태나 서버 잡이 바뀌면 목록을 갱신한다. 진행률 변화는 제외한다.
  useEffect(() => {
    // 진행률만 바뀐 emit 은 무시하고 상태·import job 변화에만 반응한다.
    let lastSignature = "";
    return uploadEngine.subscribe(() => {
      const signature = uploadEngine
        .getSnapshot()
        .jobs.map((job) => `${job.id}:${job.status}:${job.uploadJob?.uuid ?? ""}`)
        .join("|");
      if (signature === lastSignature) {
        return;
      }
      lastSignature = signature;
      void source.refresh();
    });
  }, [source]);

  // URL page 가 외부에서 바뀌면(prev/next, 뒤로/앞으로) source 를 맞춘다.
  useEffect(() => {
    applyUrlPageToSource(source, page);
  }, [source, page]);

  // 상태 필터 변경으로 source 가 page 를 1로 되돌리면 URL 에도 반영한다.
  useEffect(() => {
    mirrorSourcePageToUrl(source, page, setPage);
  }, [source, snapshot.page, page, setPage]);

  const clearDeepLink = useCallback(
    (key: "resume" | "review"): void => {
      setSearchParams(
        (previous) => {
          const next = new URLSearchParams(previous);
          next.delete(key);
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  // ?resume=/?review= 딥링크(위젯이나 다른 화면에서 진입)를 1회 읽고 해당 다이얼로그를 연다.
  // URL 파라미터는 즉시 지운다. 새로고침이나 뒤로가기에서 다이얼로그가 다시 열리지 않게 한다.
  const resumeUuid = searchParams.get("resume");
  const reviewUuid = searchParams.get("review");
  useEffect(() => {
    if (resumeUuid === null) {
      return;
    }
    clearDeepLink("resume");
    void getDatasetUpload(resumeUuid)
      .then((job) => {
        if (aliveRef.current) {
          uploadWindows.openResume(job, refreshList);
        }
      })
      .catch((error: unknown) => logger.warn("resume deep-link target load failed", error));
  }, [resumeUuid, clearDeepLink, refreshList]);

  useEffect(() => {
    if (reviewUuid === null) {
      return;
    }
    clearDeepLink("review");
    void getDatasetUpload(reviewUuid)
      .then((job) => {
        if (aliveRef.current) {
          setReviewTarget({ uuid: job.uuid, displayName: job.display_name });
        }
      })
      .catch((error: unknown) => logger.warn("review deep-link target load failed", error));
  }, [reviewUuid, clearDeepLink]);

  const handleRetry = async (row: DatasetUploadTableRow): Promise<void> => {
    if (retryingUuids.includes(row.uuid)) {
      return;
    }
    if (uploadRetryAction(row.status, row.upload_verified, row.failure) === "reupload") {
      uploadWindows.openRetry(row, refreshList);
      return;
    }
    setRetryErrorMessage(null);
    setRetryingUuids((prev) => [...prev, row.uuid]);
    try {
      await reprocessDatasetUpload(row.uuid);
      await source.refresh();
    } catch (error) {
      logger.error("reprocessDatasetUpload failed", error);
      if (aliveRef.current) {
        setRetryErrorMessage(resolveErrorMessage(error));
      }
    } finally {
      if (aliveRef.current) {
        setRetryingUuids((prev) => prev.filter((value) => value !== row.uuid));
      }
    }
  };

  // 행별 더보기 액션. 아래 세 그룹을 서로 독립으로 추가한다.
  //   - 미완료(pending_upload) 행: 이어서 업로드, 파일 검토·완료, 업로드 취소.
  //   - 재시도할 수 있는 실패형(failed/conflict) 행: 재처리 또는 재업로드.
  //   - 끝난 행(failed/cancelled/conflict/duplicate_skipped): 기록 삭제(생성자, admin).
  // 어느 그룹도 해당하지 않으면 메뉴를 열지 않는다.
  rowActionsRef.current = (row: DatasetUploadTableRow): ReactNode => {
    const isPendingUpload = row.status === "pending_upload";
    const canRetryRow = isRetryableUpload(row.status);
    const canManageRow = canManageUpload(row);
    const canDeleteRow = canManageRow && isDeletableUpload(row.status);
    if (!isPendingUpload && !canRetryRow && !canDeleteRow) {
      return null;
    }
    const foreignLease = isForeignLiveLease(row);
    const retryAction = uploadRetryAction(row.status, row.upload_verified, row.failure);
    return (
      <Menu label="작업">
        <Menu.Trigger label={row.display_name + " 추가 작업"} />
        {isPendingUpload ? (
          <>
            <Menu.Item
              disabled={foreignLease}
              caption={foreignLease ? "다른 브라우저에서 업로드 중" : undefined}
              onSelect={() => uploadWindows.openResume(row, refreshList)}
            >
              이어서 업로드
            </Menu.Item>
            <Menu.Item
              onSelect={() => setReviewTarget({ uuid: row.uuid, displayName: row.display_name })}
            >
              파일 검토·완료
            </Menu.Item>
            <Menu.Item
              disabled={!canManageRow}
              caption={canManageRow ? undefined : "다른 사용자가 시작한 업로드"}
              onSelect={() => {
                setCancelTarget({ uuid: row.uuid, displayName: row.display_name });
                setCancelBusy(false);
                setCancelErrorMessage(null);
              }}
            >
              업로드 취소
            </Menu.Item>
          </>
        ) : null}
        {canRetryRow ? (
          <Menu.Item
            disabled={retryingUuids.includes(row.uuid)}
            onSelect={() => void handleRetry(row)}
          >
            {retryAction === "reprocess" ? "재처리" : "재업로드"}
          </Menu.Item>
        ) : null}
        {canDeleteRow ? (
          <Menu.Item
            onSelect={() => {
              setDeleteTarget({ uuid: row.uuid, displayName: row.display_name });
              setDeleteBusy(false);
              setDeleteErrorMessage(null);
            }}
          >
            업로드 기록 삭제
          </Menu.Item>
        ) : null}
      </Menu>
    );
  };

  rowClickRef.current = (row: DatasetUploadTableRow): void => {
    if (row.dataset_uuid === null) {
      return;
    }
    // 리뷰 상세로 이동한다(딥링크). nav state 가 없으면 상세 화면이 단건 getDatasetReview 로 로드한다.
    void navigate("/review/datasets/" + encodeURIComponent(row.dataset_uuid));
  };

  // 업로드 취소 확인 모달의 확정. 서버 cancel 뒤 목록을 갱신한다.
  const handleCancelConfirm = async (): Promise<void> => {
    if (cancelTarget === null || cancelBusy) {
      return;
    }
    setCancelBusy(true);
    setCancelErrorMessage(null);
    try {
      await cancelDatasetUpload(cancelTarget.uuid);
      if (!aliveRef.current) {
        return;
      }
      setCancelTarget(null);
      setCancelBusy(false);
      await source.refresh();
    } catch (error) {
      if (!aliveRef.current) {
        return;
      }
      setCancelBusy(false);
      setCancelErrorMessage(resolveErrorMessage(error));
    }
  };

  // 삭제 확인 모달의 확정. 서버에서 행이 사라지므로 목록을 다시 받는다.
  const handleDeleteConfirm = async (): Promise<void> => {
    if (deleteTarget === null || deleteBusy) {
      return;
    }
    setDeleteBusy(true);
    setDeleteErrorMessage(null);
    try {
      await deleteDatasetUpload(deleteTarget.uuid);
      if (!aliveRef.current) {
        return;
      }
      setDeleteTarget(null);
      setDeleteBusy(false);
      await source.refresh();
    } catch (error) {
      if (!aliveRef.current) {
        return;
      }
      setDeleteBusy(false);
      setDeleteErrorMessage(resolveErrorMessage(error));
    }
  };

  const { loadState, size, total } = snapshot;
  // 행이 없는 목록 조회 실패는 표 본문이 알린다. 띠는 행이 남아 있는 실패와 재시도 동작의 실패를 알린다.
  const listError = tableStripError(snapshot);
  const displayError = listError ?? retryErrorMessage;
  const totalPages = Math.max(1, Math.ceil(total / size));

  // summaryInFilter 는 툴바 요약을 접고 필터 슬롯의 카드 크롬도 제거한다(민무늬).
  // 결과 수와 새로고침은 목록 상단 BulkActionBar 띠가 표시한다(rules.md §17).
  return (
    <>
      <ResourceBrowserLayout
        ariaLabel="데이터 업로드 현황 목록"
        summaryCount={total}
        summaryLabel="업로드 작업"
        summaryInFilter
        filters={
          <DatasetUploadFilterBar
            showScope={canViewAllUploads}
            filters={filters}
            onChange={handleFiltersChange}
          />
        }
        error={displayError}
        // 목록 조회 실패일 때만 다시 받는다. 재시도 동작의 실패는 목록을 다시 받아도 사라지지 않는다.
        onRetry={listError === null ? undefined : refreshList}
        footer={
          <PaginationFooter
            page={snapshot.page}
            size={size}
            total={total}
            isLoading={loadState === "loading"}
            ariaLabel="업로드 목록 페이지 이동"
            onPrevious={() => {
              if (snapshot.page > 1) {
                setPage(snapshot.page - 1);
              }
            }}
            onNext={() => {
              if (snapshot.page < totalPages) {
                setPage(snapshot.page + 1);
              }
            }}
          />
        }
      >
        {/* 목록 상단 띠. 결과 수(idle)와 새로고침을 표시한다. 행 선택이 없는 목록이라 액션은 없다. */}
        <div className={bulkActionBarStyles.host}>
          <BulkActionBar
            source={source}
            idleContent={<FilterResultCount count={total} unitLabel="업로드 작업" />}
            rightContent={
              <IconButton text="새로고침" size="sm" variant="surface" onClick={refreshList} />
            }
            ariaLabel="업로드 현황 목록 도구"
          />
          <HatDataTable
            source={source}
            columns={datasetUploadColumns}
            columnVisibility={tableColumnVisibility}
            emptyMessage="조건에 맞는 import 작업이 없습니다."
            loadingMessage="업로드 작업을 불러오고 있습니다."
            ariaLabel="데이터 업로드 현황 테이블"
            actionsColumnWidth="56px"
            scrollable
            minWidth="1080px"
          />
        </div>
      </ResourceBrowserLayout>

      {reviewTarget !== null ? (
        <StagedFilesReviewDialog
          uploadUuid={reviewTarget.uuid}
          displayName={reviewTarget.displayName}
          onClose={() => setReviewTarget(null)}
          onChanged={refreshList}
        />
      ) : null}

      {cancelTarget !== null ? (
        <HatTextModal
          open
          label="업로드를 취소할까요?"
          eyebrow={cancelTarget.displayName}
          caption="스테이징에 올라간 파일은 더 이상 사용되지 않습니다. 같은 이름으로 다시 업로드할 수 있습니다."
          error
          errorMessage={cancelErrorMessage ?? undefined}
          okText="업로드 취소"
          cancelText="닫기"
          busy={cancelBusy}
          onOkPress={() => void handleCancelConfirm()}
          onCancelPress={() => {
            setCancelTarget(null);
            setCancelErrorMessage(null);
          }}
        />
      ) : null}

      {deleteTarget !== null ? (
        <HatTextModal
          open
          label="업로드 기록을 삭제할까요?"
          eyebrow={deleteTarget.displayName}
          caption="목록에서 완전히 사라지며 되돌릴 수 없습니다. 남아 있던 업로드 파일도 함께 정리되고, 같은 이름으로 다시 업로드할 수 있습니다."
          error
          errorMessage={deleteErrorMessage ?? undefined}
          okText="삭제"
          cancelText="닫기"
          busy={deleteBusy}
          onOkPress={() => void handleDeleteConfirm()}
          onCancelPress={() => {
            setDeleteTarget(null);
            setDeleteErrorMessage(null);
          }}
        />
      ) : null}
    </>
  );
}
